import React from 'react';
import PropTypes from 'prop-types';
import ConnectedThemeProvider from '@configuratorware/configurator-frontendgui/App/Screens/Configurator/ThemeProvider';
import merge from 'lodash/merge';
import { createTheme } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import { black, green, offwhite, white } from '../Constants/Colors';

const bodyFont = '"Roboto", "Helvetica", "Arial", "sans-serif"';

const theme = createTheme({
  typography: {
    fontFamily: bodyFont,
    allVariants: {
      color: black,
    },
    h1: {
      fontSize: '36px',
      lineHeight: '42px',
      fontWeight: 400,
    },
    h2: {
      fontSize: '28px',
      lineHeight: '36px',
      fontWeight: 400,
    },
    h3: {
      fontSize: '24px',
      lineHeight: '28px',
      fontWeight: 400,
    },
    h4: {
      fontSize: '20px',
      lineHeight: '26px',
      fontWeight: 400,
    },
    h5: {
      fontSize: '18px',
      lineHeight: '24px',
      fontWeight: 400,
    },
    h6: {
      fontSize: '16px',
      lineHeight: '24px',
      fontWeight: 600,
    },
  },
  overrides: {
    MuiPaper: {
      rounded: {
        borderRadius: 0,
      },
    },
    MuiCssBaseline: {
      '@global': {
        // Reserves the scrollbar's width whether or not it's actually rendered, so opening a
        // modal (which hides the page scrollbar) doesn't shift the whole layout sideways.
        html: { scrollBehavior: 'smooth', scrollbarGutter: 'stable' },
        a: {
          color: green,
        },
        ul: {
          listStyleType: 'square',
        },
        'ul ul': {
          listStyleType: 'disc',
        },
        'ul ul ul': {
          listStyleType: 'circle',
        },
        'li::marker': {
          color: green,
        },
        '::selection': {
          backgroundColor: green,
          color: white,
        },
      },
    },
  },
});

const overrideTheme = (theme) => {
  merge(theme.overrides, {
    Progress: {
      container: {
        backgroundColor: 'transparent',
      },
    },
    Thumbnail: {
      zIndex: 1,
      dots: {
        justifyContent: 'end',
      },
    },
    AlignTargetContainer: {
      root: {
        position: 'relative',
      },
    },
    CustomDialog: {
      MuiDialogBorder: {
        borderTopColor: 'transparent',
      },
    },
    MuiDialogActions: {
      root: {
        justifyContent: 'flex-end',
      },
      '& .MuiButtonBase-root': {
        boxShadow: 0,
      },
    },
    LoadConfiguration: {
      codeField: {
        width: '100%',
      },
    },
    SaveConfiguration: {
      successResult: {
        background: green,
        borderRadius: 'none',
      },
    },
    AmountPrice: {
      root: {
        position: 'inherit',
        [theme.breakpoints.between('xs', 'sm')]: {
          width: 'auto',
        },
        [theme.breakpoints.between('sm', 'xl')]: {
          width: 'auto',
        },
      },
    },
    MuiButton: {
      root: {
        fontSize: 12,
        borderColor: green,
      },
      contained: {
        backgroundColor: green,
        borderRadius: 0,
        color: white,
        '&:hover': {
          backgroundColor: black,
          [theme.breakpoints.down('sm')]: {
            backgroundColor: green,
          },
        },
        '&:disabled': {
          backgroundColor: green,
          opacity: 0.5,
          color: white,
        },
      },
      outlined: {
        borderColor: green,
        color: green,
        '&:hover': {
          borderColor: black,
          color: black,
          [theme.breakpoints.down('sm')]: {
            borderColor: green,
            color: green,
          },
        },
        '&:disabled': {
          borderColor: black,
          color: green,
          opacity: 0.5,
        },
      },
    },
  });

  // MainButton (a shared component, e.g. the in-room "Show bed in room" CTA) reads
  // theme.buttons directly rather than theme.overrides.MuiButton, so it needs its own
  // override to match the rest of the app's green/black button convention.
  merge(theme.buttons, {
    mainBtn: {
      backgroundColor: green,
    },
    mainBtnHover: {
      backgroundColor: black,
    },
  });
};

export default function ThemeProvider({ children }) {
  return (
    <ConnectedThemeProvider customTheme={theme} withTheme={overrideTheme}>
      <CssBaseline />
      {children}
    </ConnectedThemeProvider>
  );
}

ThemeProvider.propTypes = {
  children: PropTypes.node,
};
